/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FormEventHandler, KeyboardEventHandler, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { AttachButton, MessageInput, SendButton, SendForm, coreDialogApi, useDialog } from '@cp/entities/dialog';
import { SxProps } from '@cp/ds';
import { handleApiServerError } from '@cp/utils/apiServer/errorHandler';
import { createFromValidationResolver } from '@cp/utils/form/resolver';
import { HttpStatusCode } from '@cp/utils/const/httpStatusCode';
import { AnnounceHintWrapper } from '@cp/ds/src/components/announceHintWrapper';
import { useInjection } from '@cp/shared/di';
import { ITempMessageProvider, MessageFormValues, generateCreateMessageRequest } from '../../model';
import { PAYMENT_FEATURE } from '../../../di';

export interface SendMessageFormProps {
  sx?: SxProps;
  onSent?: () => void;
  allowAttachments?: boolean;
  tempMessageProvider?: ITempMessageProvider;
}
const resolver = createFromValidationResolver(MessageFormValues);

export const SendMessageForm = React.forwardRef<HTMLFormElement, SendMessageFormProps>(
  ({ onSent, allowAttachments, sx, tempMessageProvider }, ref) => {
    const onPaymentRequired = useInjection(PAYMENT_FEATURE);
    const isMobile = useIsMobile();
    const enableSendByEnter = !isMobile;
    const dialog = useDialog();
    const form = useForm<MessageFormValues>({
      mode: 'onSubmit',
      shouldFocusError: false,
      resolver,
      defaultValues: {
        dialogId: dialog?.id,
        message: tempMessageProvider ? tempMessageProvider.get() : '',
      },
    });
    const message = form.watch('message');
    const [sendMessage] = coreDialogApi.useSendMessageMutation();

    const handleSendMessage = useCallback(
      async (formValues: MessageFormValues) => {
        const dto = generateCreateMessageRequest({ ...formValues, dialogId: dialog?.id as number });
        const response = await sendMessage(dto);
        if ('error' in response) {
          if ('status' in response.error && response.error.status === HttpStatusCode.PaymentRequired) {
            return onPaymentRequired();
          }
          handleApiServerError(response.error, { form, formFieldNameForCommonErrors: 'message' });
        } else {
          tempMessageProvider?.clear();
          form.resetField('message', { defaultValue: '' });
          onSent?.();
        }
      },
      [dialog?.id, sendMessage, form, onPaymentRequired, tempMessageProvider, onSent],
    );

    useEffect(() => {
      if (dialog?.id) {
        form.setValue('dialogId', dialog.id);
      }
    }, [form, dialog?.id]);

    const triggerSubmit = useCallback(
      form.handleSubmit(handleSendMessage, () => {
        // Do not show validation errors for message
        form.clearErrors('message');
      }),
      [handleSendMessage, form],
    );

    const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
      (e) => {
        e.preventDefault();
        triggerSubmit();
        form.setFocus('message');
      },
      [form, triggerSubmit],
    );

    const onKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
      (e) => {
        if (!enableSendByEnter || e.key !== 'Enter' || e.ctrlKey || e.shiftKey || e.metaKey) {
          return;
        }
        e.preventDefault();
        triggerSubmit();
      },
      [enableSendByEnter, triggerSubmit],
    );

    const attachment =
      allowAttachments && (!isMobile || !message.length) ? (
        <AnnounceHintWrapper>
          {({ setAnnounceMessageVisible }) => <AttachButton onAttach={() => setAnnounceMessageVisible(true)} />}
        </AnnounceHintWrapper>
      ) : null;

    return (
      <FormProvider {...form}>
        <SendForm onSubmit={onSubmit} ref={ref} sx={sx}>
          {attachment}
          <MessageInput
            disabled={!dialog?.id}
            form={form}
            messageFieldProps={{ onKeyUp: (e) => tempMessageProvider?.set(e.currentTarget.value) }}
            onKeyDown={onKeyDown}
          />
          <SendButton disabled={!form.formState.isValid || form.formState.isSubmitting} size={isMobile ? 40 : 48} />
        </SendForm>
      </FormProvider>
    );
  },
);
